import { useTranslate } from '@refinedev/core';
import { Card, Flex, Grid, Tabs, Typography, theme } from 'antd';
import dayjs from 'dayjs';
import type { ChargeMonthlyOut } from '@/api/generated/schemas';
import { StatCell } from '@/components/common/show/StatCell';
import { ProfileIconLink } from '@/components/account/ProfileIconLink';
import { ProductFormatTag } from '@/components/user_catalog/publications/ProductFormatTag';
import { PublicationByProductLink } from '@/components/user_catalog/publications/PublicationByProductLink';
import { productKind } from '@/components/user_catalog/publications/productKind';
import { usePublicationByProduct } from '@/components/user_catalog/publications/usePublicationByProduct';
import { PublisherIconLink } from '@/components/user_catalog/publishers/PublisherIconLink';
import { PaymentMomentTag } from '@/components/tenancy/tags';
import { formatDuration } from '@/utils/duration';
import { formatLedger, formatPayable } from '@/utils/money';
import { UnitPriceCell } from '@/components/settlement/UnitPriceCell';
import { CHARGE_MONTHLY_SHOW_TABS } from '../constants';
import type { ChargeMonthlyShowTab } from '../constants';

const { Text, Title } = Typography;

const T = 'settlement.charges_monthly';

const TAB_LABEL_KEYS: Record<ChargeMonthlyShowTab, string> = {
  general: 'base.sections.basic_info',
  daily: `${T}.sections.daily`,
};

interface ChargeMonthlyShowHeaderProps {
  record: ChargeMonthlyOut;
  activeTab: ChargeMonthlyShowTab;
  onTabChange: (key: string) => void;
}

export const ChargeMonthlyShowHeader = ({
  record,
  activeTab,
  onTabChange,
}: ChargeMonthlyShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  // Shares its request with the title link below — same hook, same query key.
  const kind = productKind(
    usePublicationByProduct(record.product_id),
    record.product_id
  );

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex gap={token.marginXS} align="baseline" wrap>
            <Title
              level={3}
              style={{ margin: 0, fontSize: token.fontSizeHeading3 }}
            >
              {dayjs(record.bucket).format('YYYY-MM')}
            </Title>
            <Text type="secondary">{'·'}</Text>
            <span style={{ fontSize: token.fontSizeHeading5 }}>
              <PublicationByProductLink productId={record.product_id} />
            </span>
            {kind && <ProductFormatTag kind={kind} />}
          </Flex>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`${t(`${T}.fields.product_id`)} ${record.product_id} · ${t(
              `${T}.fields.product_version`
            )} ${record.product_version}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t(`${T}.fields.publisher_id`)}>
              <PublisherIconLink publisherId={record.publisher_id} />
            </StatCell>
            <StatCell label={t(`${T}.fields.profile_id`)}>
              <ProfileIconLink profileId={record.profile_id} />
            </StatCell>
            <StatCell label={t(`${T}.fields.amount`)} size="lg">
              {formatLedger(record.amount, record.currency)}
            </StatCell>
            <StatCell label={t(`${T}.fields.unit_price`)}>
              <UnitPriceCell
                unitPrice={record.unit_price}
                currency={record.currency}
              />
            </StatCell>
            <StatCell label={t(`${T}.fields.payable_unit_price`)}>
              {formatPayable(record.payable_unit_price, record.currency)}
            </StatCell>
            <StatCell label={t(`${T}.fields.basis_seconds`)}>
              {formatDuration(record.basis_seconds)}
            </StatCell>
            <StatCell label={t(`${T}.fields.charges_count`)}>
              {`${record.charges_count}`}
            </StatCell>
            <StatCell label={t(`${T}.fields.payment_moment`)}>
              <PaymentMomentTag moment={record.payment_moment} />
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={CHARGE_MONTHLY_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
